import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ITeamWithChannels } from '../../../../entities/IClusters';
import { getTeamsWithChannels } from '../../../../actions/clustersActions';
import { useClusterContext } from './ClusterContext';

type GetTeamsWithChannels = ReturnType<typeof getTeamsWithChannels>;

interface IActivityContextProps {
    teamsAndChannels: ITeamWithChannels[];
    currentChannelId: string;
    loadingTeamsAndChannels: boolean;
    isChannelsReady: boolean;
    setTeamsAndChannels: React.Dispatch<React.SetStateAction<ITeamWithChannels[]>>;
    setCurrentChannelId: (id: string) => void;
    clearActivityContext: () => void;
}

const ActivityContext = createContext<IActivityContextProps | undefined>(undefined);

export const useActivityContext = () => {
    const context = useContext(ActivityContext);
    if (!context) {
        throw new Error('useActivityContext must be used within an ActivityContextProvider');
    }
    return context;
};

export const useOptionalActivityContext = (): IActivityContextProps | undefined => useContext(ActivityContext);

export const ActivityContextProvider = ({ children }) => {
    const dispatch = useDispatch();
    const clusterContext = useClusterContext();
    const [previousClusterId, setPreviousClusterId] = React.useState<string>(undefined);
    const [teamsAndChannels, setTeamsAndChannels] = React.useState<ITeamWithChannels[]>(undefined);
    const [loadingTeamsAndChannels, setLoadingTeamsAndChannels] = React.useState<boolean>(false);
    const [currentChannelId, setCurrentChannelId] = React.useState<string>('');

    const clearActivityContext = () => {
        setTeamsAndChannels(undefined);
        setCurrentChannelId('');
    };

    const value: IActivityContextProps = useMemo(() => ({
        teamsAndChannels,
        currentChannelId,
        loadingTeamsAndChannels,
        isChannelsReady: !loadingTeamsAndChannels && !!teamsAndChannels?.length,
        setTeamsAndChannels,
        setCurrentChannelId,
        clearActivityContext
    }), [teamsAndChannels, currentChannelId, loadingTeamsAndChannels]);

    useEffect(() => {
        if (!loadingTeamsAndChannels && clusterContext.currentCluster && clusterContext.currentClusterId && clusterContext.currentClusterId !== previousClusterId) {
            clearActivityContext();
            setPreviousClusterId(clusterContext.currentClusterId);
            setLoadingTeamsAndChannels(true);
            dispatch<GetTeamsWithChannels>(getTeamsWithChannels(clusterContext.currentClusterId)).then((res: ITeamWithChannels[]) => {
                setTeamsAndChannels(res);
                const channelId = res?.[0]?.teamChannels?.[0]?.id;
                if (channelId) {
                    setCurrentChannelId(channelId);
                }
            }).finally(() => {
                setLoadingTeamsAndChannels(false);
            });
        } else if (!loadingTeamsAndChannels && clusterContext.currentCluster && clusterContext.currentClusterId &&
                 clusterContext.currentClusterId === previousClusterId && !teamsAndChannels?.length) {
            setLoadingTeamsAndChannels(true);
            dispatch<GetTeamsWithChannels>(getTeamsWithChannels(clusterContext.currentClusterId)).then((res: ITeamWithChannels[]) => {
                setTeamsAndChannels(res);
                const channelId = res?.[0]?.teamChannels?.[0]?.id;
                if (channelId) {
                    setCurrentChannelId(channelId);
                }
            }).finally(() => {
                setLoadingTeamsAndChannels(false);
            });
        }
    }, [clusterContext.currentCluster, clusterContext.currentClusterId, previousClusterId, dispatch, teamsAndChannels]);

    return (
        <ActivityContext.Provider value={value}>
            {children}
        </ActivityContext.Provider>
    );
};

export default ActivityContext;